// SPDX-License-Identifier: MIT
// B3 — « Redéployer » orchestration for the deploy-expiry alert banner.
// Re-runs the EXISTING deploy-frontend path (same subdomain, no backend
// re-provision), repaints immediately so the click has a visible response,
// then polls to a terminal state. Only a deploy that actually re-reached
// `live` mirrors the +14 j expiry locally — an optimistic mirror of the
// server-side ttlDays:14 that Part C's status-poll persist supersedes with
// the ledger's real value. Injected deps keep welcome.ts wiring thin and
// this unit-testable without timers or network.

export interface RedeployDeps {
  /** Reads the row: a deploy is already building (wizard or banner). */
  isBuilding: () => boolean;
  /** The existing deploy path (deployFrontendForRow, model 'none'). */
  deploy: (handle: string) => Promise<unknown>;
  /** One status poll (pollDeployStatusForRow — updates the row/cache). */
  poll: () => Promise<unknown>;
  /** Reads the row: the redeploy ended live. */
  becameLive: () => boolean;
  /** Writes the optimistic +14 j mirror on the row + cache. */
  applyExpiry: (iso: string) => void;
  /** Re-renders the online section (caller gates on the active view). */
  repaint: () => void;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

/** Server contract: every deploy-frontend call sets ttlDays: 14. */
export const REDEPLOY_TTL_DAYS = 14;
const POLL_MS = 2500; // same cadence as the mise-en-ligne wizard
const MAX_POLLS = 120; // ~5 min window, then give up honestly (stays 'building')

export async function redeployFromBanner(handle: string, deps: RedeployDeps): Promise<void> {
  if (deps.isBuilding()) return; // one deploy at a time
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => { setTimeout(r, ms); }));
  const now = deps.now ?? Date.now;
  let triggered = false;
  try {
    await deps.deploy(handle);
    triggered = true;
  } catch (e) {
    console.warn('[deploy-expiry] redeploy failed', e);
  }
  deps.repaint(); // building — or unchanged when the trigger failed
  if (!triggered) return;
  for (let i = 0; i < MAX_POLLS && deps.isBuilding(); i += 1) {
    await sleep(POLL_MS);
    try {
      await deps.poll();
    } catch {
      /* transient poll failure — keep building, retry next tick */
    }
  }
  if (deps.becameLive()) {
    deps.applyExpiry(new Date(now() + REDEPLOY_TTL_DAYS * 86_400_000).toISOString());
  }
  deps.repaint();
}

/**
 * Click-handler factory with an in-flight guard: a double-click while the
 * first redeploy is still triggering fires only ONE deploy (same bug class as
 * the resume panel's double-clone).
 */
export function makeRedeployHandler(getHandle: () => string, deps: RedeployDeps): () => void {
  let inFlight = false;
  return () => {
    if (inFlight || deps.isBuilding()) return;
    inFlight = true;
    void redeployFromBanner(getHandle(), deps).finally(() => { inFlight = false; });
  };
}
